package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import dev.lingvo.shared.StoreAppDto
import dev.lingvo.shared.StoreDiffDto
import dev.lingvo.shared.StoreField
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreFieldDiffDto
import dev.lingvo.shared.StoreLocaleDiffDto
import dev.lingvo.shared.StorePushRequest
import dev.lingvo.shared.StorePushStatus
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * One bound app's diff: per store language, every field as the store has it beside what Lingvo
 * would send, and the push that sends the reviewed version.
 *
 * The push presents the diff's `diffToken`. If anything changed after the reviewer read this —
 * someone edited a field, or the store itself changed — the server refuses with `store_diff_stale`,
 * and the page reloads the diff and says so rather than pushing text nobody looked at.
 */
@Composable
internal fun StoreDiffSection(
    app: StoreAppDto,
    subject: ProjectAuthoritySubject?,
    slug: String,
    revision: Int,
    runner: StoreActionRunner,
    onListingChanged: () -> Unit,
) {
    val platform = app.platform
    var localReload by remember(subject, app.id) { mutableStateOf(0) }
    var stale by remember(subject, app.id) { mutableStateOf(false) }
    val state = loadReloadable(subject, app.id, reloadToken = revision + localReload) {
        val current = subject ?: awaitCancellation()
        requestProjectResource(key = current.action, expectedSlug = slug) { route ->
            ApiClient.storeDiff(route.project.slug, platform)
        }
    }

    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LSectionTitle(platform.displayName)
        if (stale) {
            LAlert(
                t("app.projects.slug.stores_diff.stale_title", "The text changed since you reviewed it"),
                t(
                    "app.projects.slug.stores_diff.stale_detail",
                    "Nothing was pushed. Someone edited the listing, or the store changed, after this comparison was loaded. " +
                        "The fresh comparison is below; review it, then push again.",
                ),
                variant = LAlertVariant.WARNING,
                live = true,
                onDismiss = { stale = false },
            )
        }
        when (val snapshot = state.state) {
            Async.Loading -> LoadingState(rows = 3)
            is Async.Failure -> if (snapshot.isRefusal || snapshot.isMissing) {
                AsyncFailureState(
                    snapshot,
                    subject = t("app.projects.slug.stores_diff.the_comparison", "the store comparison"),
                )
            } else {
                LAlert(
                    t("app.projects.slug.stores_diff.couldnt_compare", "Couldn't compare with the store"),
                    storeErrorMessage(
                        ApiException(snapshot.status ?: 0, snapshot.code.orEmpty(), snapshot.message),
                        tr("app.projects.slug.stores_diff.couldnt_compare_detail", "The store didn't answer. Retry in a moment."),
                    ),
                    variant = LAlertVariant.DANGER,
                    action = {
                        LButton(
                            t("app.projects.slug.stores_diff.retry", "Retry"),
                            onClick = { localReload++ },
                            variant = LButtonVariant.SECONDARY,
                        )
                    },
                )
            }
            is Async.Success -> StoreDiffBody(
                diff = snapshot.value,
                slug = slug,
                runner = runner,
                onStale = {
                    stale = true
                    localReload++
                },
                onPushed = {
                    stale = false
                    onListingChanged()
                },
            )
        }
    }
}

@Composable
private fun StoreDiffBody(
    diff: StoreDiffDto,
    slug: String,
    runner: StoreActionRunner,
    onStale: () -> Unit,
    onPushed: () -> Unit,
) {
    var selected by remember(diff.diffToken) { mutableStateOf(defaultPushSelection(diff)) }
    var confirming by remember(diff.diffToken) { mutableStateOf(false) }
    var busy by remember(diff.diffToken) { mutableStateOf(false) }
    val sending = pushableSelection(diff, selected)
    val blocked = pushBlockedReason(diff, selected)

    if (!diff.editable) {
        LAlert(
            t("app.projects.slug.stores_diff.locked_title", "The store accepts only some fields right now"),
            listOfNotNull(
                diff.stateNote?.takeIf(String::isNotBlank),
                tr(
                    "app.projects.slug.stores_diff.locked_detail",
                    "Fields the store has locked are queued and sent once it accepts them; the rest go now.",
                ),
            ).joinToString(" "),
            variant = LAlertVariant.INFO,
        )
    }
    if (diff.locales.isEmpty()) {
        LMuted(
            t(
                "app.projects.slug.stores_diff.no_locales",
                "Nothing to compare yet. Import the current listing to bring the store's text into the project.",
            ),
        )
    }
    diff.locales.forEach { locale ->
        StoreLocaleBlock(
            locale = locale,
            checked = locale.storeLocale in selected,
            enabled = runner.canManage && !busy,
            onCheckedChange = { on ->
                selected = if (on) selected + locale.storeLocale else selected - locale.storeLocale
            },
            fitKeywords = if (diff.platform == StorePlatform.APP_STORE && locale.fields.any { it.field == StoreField.APP_KEYWORDS }) {
                {
                    runner.run(
                        setBusy = { busy = it },
                        failure = tr("app.projects.slug.stores_diff.fit_failed", "Couldn't fit the keywords. Retry?"),
                        transport = { ApiClient.fitStoreKeywords(slug, locale.projectLocale) },
                    ) { plan ->
                        runner.notify(storeKeywordPlanMessage(plan), LToastVariant.SUCCESS, TOAST_UNTIL_DISMISSED)
                        onPushed()
                    }
                }
            } else {
                null
            },
        )
    }
    Row(
        Modifier.fillMaxWidth().gap(Space.MD).flexWrap(FlexWrap.Wrap),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LButton(
            tf("app.projects.slug.stores_diff.push_to", "Push to {store}", "store" to diff.platform.displayName),
            onClick = { confirming = true },
            enabled = runner.canManage && blocked == null && !busy,
            loading = busy,
        )
        when {
            !runner.canManage -> LMuted(
                t("app.projects.slug.stores_diff.push_needs_admin", "Only an owner or admin can push to a live store."),
            )
            blocked != null -> LMuted(blocked)
            else -> LMuted(
                tPlural(
                    "app.projects.slug.stores_diff.count.push_summary",
                    sending.size,
                    "{count} language will be sent.",
                    "{count} languages will be sent.",
                ),
            )
        }
    }
    LConfirmDialog(
        open = confirming,
        title = tf("app.projects.slug.stores_diff.confirm_title", "Push to {store}?", "store" to diff.platform.displayName),
        confirmLabel = t("app.projects.slug.stores_diff.confirm_push", "Push now"),
        onConfirm = {
            val request = StorePushRequest(diffToken = diff.diffToken, locales = sending)
            runner.run(
                setBusy = { busy = it },
                failure = tr("app.projects.slug.stores_diff.push_failed", "Couldn't push to the store. Retry?"),
                transport = { ApiClient.pushStoreListing(slug, diff.platform, request) },
                onError = { error ->
                    confirming = false
                    if (error.isStoreCode(STORE_DIFF_STALE)) {
                        onStale()
                        true
                    } else {
                        false
                    }
                },
            ) { push ->
                confirming = false
                val (message, variant) = when (push.status) {
                    StorePushStatus.SUCCEEDED -> tr("app.projects.slug.stores_diff.pushed_toast", "Pushed. The store has the new text.") to LToastVariant.SUCCESS
                    StorePushStatus.PARTIAL -> tr("app.projects.slug.stores_diff.partial_toast", "Some languages were pushed and some failed. See the history.") to LToastVariant.WARNING
                    StorePushStatus.QUEUED -> (push.queuedReason ?: tr("app.projects.slug.stores_diff.queued_toast", "Queued until the store accepts these fields.")) to LToastVariant.NEUTRAL
                    StorePushStatus.FAILED -> tr("app.projects.slug.stores_diff.failed_toast", "The store refused the push. See the history for why.") to LToastVariant.DANGER
                    StorePushStatus.PENDING -> tr("app.projects.slug.stores_diff.pending_toast", "Push started.") to LToastVariant.NEUTRAL
                }
                runner.notify(message, variant, if (variant == LToastVariant.SUCCESS) 4_000 else TOAST_UNTIL_DISMISSED)
                onPushed()
            }
        },
        onCancel = { confirming = false },
        tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.stores_diff.confirm_consequence_title", "This text goes live"),
        consequence = tf(
            "app.projects.slug.stores_diff.confirm_consequence",
            "Customers see this text in {locales} as soon as the store accepts it. Text that replaces what the store has can be rolled back from the history below.",
            "locales" to sending.joinToString(", "),
        ) + storeIrreversibleFieldCount(diff, sending).let { fresh ->
            if (fresh == 0) {
                ""
            } else {
                " " + tPlural(
                    "app.projects.slug.stores_diff.confirm_irreversible",
                    fresh,
                    "{count} field is new to the store, so a rollback cannot take it back.",
                    "{count} fields are new to the store, so a rollback cannot take them back.",
                )
            }
        },
        busy = busy,
    )
}

/** One store language: its choice to push, its fields, and the refusals that block it. */
@Composable
private fun StoreLocaleBlock(
    locale: StoreLocaleDiffDto,
    checked: Boolean,
    enabled: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    /** Present for an App Store language that has a keyword field: fits the draft to 100 bytes. */
    fitKeywords: (() -> Unit)? = null,
) {
    val language = localeInfo(locale.projectLocale).englishName
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (locale.pushable) {
                    LCheckbox(
                        checked = checked,
                        onCheckedChange = onCheckedChange,
                        label = trf(
                            "app.projects.slug.stores_diff.push_locale",
                            "Push {language} ({locale})",
                            "language" to language,
                            "locale" to locale.storeLocale,
                        ),
                        enabled = enabled,
                    )
                } else {
                    Text(
                        trf(
                            "app.projects.slug.stores_diff.locale_heading",
                            "{language} ({locale})",
                            "language" to language,
                            "locale" to locale.storeLocale,
                        ),
                    )
                    val held = storeBlockedFieldCount(locale)
                    if (held > 0 || locale.issues.isNotEmpty()) {
                        LBadge(
                            if (held > 0) {
                                tPlural(
                                    "app.projects.slug.stores_diff.blocked_fields",
                                    held,
                                    "Blocked: {count} field needs attention",
                                    "Blocked: {count} fields need attention",
                                )
                            } else {
                                t("app.projects.slug.stores_diff.blocked", "Blocked")
                            },
                            variant = LBadgeVariant.WARNING,
                        )
                    } else {
                        LBadge(t("app.projects.slug.stores_diff.nothing_to_push", "Nothing to push"))
                    }
                }
            }
            // A five-column comparison cannot fit a phone: the cells shrink until words break mid-letter.
            // Wide screens keep the table; narrow ones get one stacked card per field.
            Div(Modifier.fillMaxWidth().displayIfAtLeast(Breakpoint.LG).toAttrs()) { StoreFieldTable(locale, language) }
            Div(Modifier.fillMaxWidth().displayUntil(Breakpoint.LG).toAttrs()) { StoreFieldCards(locale) }
            if (fitKeywords != null) {
                Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                    LButton(
                        t("app.projects.slug.stores_diff.fit_keywords", "Fit keywords"),
                        onClick = fitKeywords,
                        variant = LButtonVariant.SECONDARY,
                        size = LButtonSize.SM,
                        enabled = enabled,
                    )
                    LMuted(
                        t(
                            "app.projects.slug.stores_diff.fit_keywords_hint",
                            "Drops words the name or subtitle already index and repeats, and keeps the rest within 100 bytes. The result is a draft you approve.",
                        ),
                    )
                }
            }
            if (storeBlockedFieldCount(locale) > 0) {
                LMuted(
                    t(
                        "app.projects.slug.stores_diff.blocked_hint",
                        "Approve the fields marked Not reviewed in Translate, or shorten any over the limit, then reload this comparison. Nothing in this language is pushed until all of them are clear.",
                    ),
                )
            }
            if (locale.issues.isNotEmpty()) {
                LAlert(
                    t("app.projects.slug.stores_diff.issues_title", "Blocking this language"),
                    locale.issues.joinToString(" "),
                    variant = LAlertVariant.DANGER,
                    dense = true,
                )
            }
        }
    }
}

@Composable
private fun StoreFieldTable(locale: StoreLocaleDiffDto, language: String) {
    LTable(
        caption = tf("app.projects.slug.stores_diff.table_caption", "{language} listing fields", "language" to language),
        captionHidden = true,
    ) {
        LTableHead {
            LTableRow {
                LTableHeaderCell { Text(t("app.projects.slug.stores_diff.field", "Field")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_diff.in_the_store", "In the store")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_diff.in_lingvo", "In Lingvo")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_diff.length", "Length")) }
                LTableHeaderCell { Text(t("app.projects.slug.stores_diff.status", "Status")) }
            }
        }
        LTableBody {
            locale.fields.forEach { field -> StoreFieldRow(field, locale.projectLocale) }
        }
    }
}

/** The same comparison as [StoreFieldTable], one field per card, for screens too narrow for five columns. */
@Composable
private fun StoreFieldCards(locale: StoreLocaleDiffDto) {
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        locale.fields.forEach { field ->
            val meter = storeLengthMeter(field)
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    Row(
                        Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(storeFieldLabel(field.field))
                        LBadge(storeFieldStatusLabel(field.status), variant = storeFieldStatusVariant(field.status))
                        if (field.field.reviewOnly) {
                            LBadge(
                                t("app.projects.slug.stores_diff.draft_review", "Draft — review before pushing"),
                                variant = LBadgeVariant.WARNING,
                            )
                        }
                    }
                    LMuted(t("app.projects.slug.stores_diff.in_the_store", "In the store"))
                    StoreFieldText(field.remote, t("app.projects.slug.stores_diff.not_in_store", "Not in the store yet"), locale.projectLocale)
                    LMuted(t("app.projects.slug.stores_diff.in_lingvo", "In Lingvo"))
                    StoreFieldText(field.local, t("app.projects.slug.stores_diff.not_written", "Not written in Lingvo"), locale.projectLocale)
                    if (meter.over) {
                        LBadge(storeLengthMeterText(meter), variant = LBadgeVariant.DANGER)
                    } else {
                        LMuted(storeLengthMeterText(meter))
                    }
                }
            }
        }
    }
}

@Composable
private fun StoreFieldRow(field: StoreFieldDiffDto, projectLocale: String) {
    val meter = storeLengthMeter(field)
    LTableRow {
        LTableCell {
            Column(Modifier.gap(Space.XXS)) {
                Text(storeFieldLabel(field.field))
                if (field.field.reviewOnly) {
                    LBadge(
                        t("app.projects.slug.stores_diff.draft_review", "Draft — review before pushing"),
                        variant = LBadgeVariant.WARNING,
                    )
                }
            }
        }
        LTableCell {
            StoreFieldText(field.remote, t("app.projects.slug.stores_diff.not_in_store", "Not in the store yet"), projectLocale)
        }
        LTableCell {
            StoreFieldText(field.local, t("app.projects.slug.stores_diff.not_written", "Not written in Lingvo"), projectLocale)
        }
        LTableCell {
            if (meter.over) {
                LBadge(storeLengthMeterText(meter), variant = LBadgeVariant.DANGER)
            } else {
                Text(storeLengthMeterText(meter))
            }
        }
        LTableCell {
            LBadge(storeFieldStatusLabel(field.status), variant = storeFieldStatusVariant(field.status))
        }
    }
}

/** Listing text as written: its line breaks kept, a 4,000-character description scrolled, not cut. */
@Composable
private fun StoreFieldText(value: String?, absent: String, lang: String) {
    if (value.isNullOrEmpty()) {
        LMuted(absent)
        return
    }
    Div(
        Modifier
            .styleModifier {
                property("white-space", "pre-wrap")
                property("overflow-wrap", "anywhere")
                property("max-height", "12em")
                property("overflow", "auto")
            }
            .toAttrs {
                attr("dir", "auto")
                attr("lang", lang)
            },
    ) {
        Text(value)
    }
}
